import { FC, Ref, useRef } from "react";

import { SidebarOptionsMenu, SidebarOptionsMenuProps } from "./SidebarOptionsMenu";

import { COURSE_CODE_GROUPING_ID_QUERY_PARAM } from "components/constants";
import { CourseGroupingType } from "components/server-types";
import { t } from "i18n/i18n";
import { IconText } from "mds/components/IconText";
import { MenuInstance, MenuItem } from "mds/components/Menu";
import { showConfirmationModal } from "mds/components/Modal";
import { PencilEditIcon } from "mds/icons";
import { storeApi, useAppSelector } from "store/index";
import { selectCourseCodesByGroupingId } from "store/selectors/section";
import { toastSuccessMessage } from "utils/alerts";
import { useQueryParamsManager } from "utils/routing";

interface CourseGroupingOptionsMenuProps extends SidebarOptionsMenuProps {
  courseCodeGrouping: CourseGroupingType;
  onRename: () => void;
}

export const CourseCodeGroupingOptionsMenu: FC<CourseGroupingOptionsMenuProps> = ({
  courseCodeGrouping,
  onRename,
  className,
  eventPrefix,
}) => {
  const menuRef: Ref<MenuInstance> = useRef(null);
  const courseCodes = useAppSelector((state) =>
    selectCourseCodesByGroupingId(state, courseCodeGrouping.id),
  );
  const { deleteQueryParams } = useQueryParamsManager();
  const hasCourseCodes = courseCodes.length > 0;

  const handleDelete = async () => {
    if (hasCourseCodes) {
      return;
    }

    const didDelete = await showConfirmationModal({
      title: t("confirm_delete_modal.delete_item", { item: t("glossary.course_code_grouping") }),
      labelText: t("confirm_delete_modal.delete_label", { name: courseCodeGrouping.title }),
    });

    if (didDelete) {
      await storeApi.course_code_groupings.destroy(courseCodeGrouping.id);

      toastSuccessMessage(t("home.toasts.grouping_deleted"));

      deleteQueryParams([COURSE_CODE_GROUPING_ID_QUERY_PARAM]);
    }
  };

  return (
    <SidebarOptionsMenu
      className={className}
      eventPrefix={eventPrefix}
      instanceRef={menuRef}
      kind="gray"
      optionsMenuActions={{
        onDelete: handleDelete,
        isDeleteDisabled: hasCourseCodes,
        deleteTooltipText: hasCourseCodes
          ? t("sidebar_options_menu.cannot_delete_grouping_with_course_codes")
          : undefined,
      }}
      size="s"
    >
      <MenuItem onClick={onRename}>
        <IconText
          iconStart={<PencilEditIcon />}
          text={t("sidebar_options_menu.rename_course_code_grouping")}
        />
      </MenuItem>
    </SidebarOptionsMenu>
  );
};
